import {
  DndContext,
  DragOverlay,
  DragStartEvent,
  Modifier,
  MouseSensor,
  pointerWithin,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { getEventCoordinates } from '@dnd-kit/utilities';
import React, { useState, ReactNode, useCallback } from 'react';
import { createPortal } from 'react-dom';

import { getMenuEditorDragEventData } from './utils';

type MenuEditorDnDContextProps = {
  children: ReactNode;
  menuEditorRootUrl: string;
};

const snapDragOverlayToPointer: Modifier = ({ activatorEvent, draggingNodeRect, transform, active, over }) => {
  const activeEvent = active && getMenuEditorDragEventData(active);
  const overEvent = over && getMenuEditorDragEventData(over);

  if (!draggingNodeRect || !activatorEvent || !activeEvent || !overEvent) {
    return transform;
  }

  const activatorCoordinates = getEventCoordinates(activatorEvent);

  if (!activatorCoordinates) {
    return transform;
  }

  if ('shrinkOverlayTypes' in overEvent && overEvent.shrinkOverlayTypes.includes(activeEvent.type)) {
    const yModifier = (activatorCoordinates.y - draggingNodeRect.top - draggingNodeRect.height / 2) / 2;

    return {
      ...transform,
      y: transform.y + yModifier,
      scaleX: 0.5,
      scaleY: 0.5,
    };
  }

  const yModifier = activatorCoordinates.y - draggingNodeRect.top - draggingNodeRect.height / 2;

  return {
    ...transform,
    y: transform.y + yModifier,
    // Prevent drag overlay scale based on the hovering object due to DragOverlay.adjustScale is enabled.
    scaleX: 1,
    scaleY: 1,
  };
};

const MenuEditorDnDContext = ({ children }: MenuEditorDnDContextProps) => {
  const [dragOverlay, setDragOverlay] = useState<null | ReactNode>(null);
  const sensors = useSensors(useSensor(MouseSensor, { activationConstraint: { distance: 5 } }));

  const handleDragStart = useCallback(({ active }: DragStartEvent) => {
    const activeEvent = active && getMenuEditorDragEventData(active);
    if (
      activeEvent?.type === 'DND_NAVIGATION_CATEGORY' ||
      activeEvent?.type === 'DND_EDITOR_SINGLE_ITEM' ||
      activeEvent?.type === 'DND_EDITOR_MULTIPLE_ITEMS' ||
      activeEvent?.type === 'DND_EDITOR_SINGLE_NO_CATEGORY_ITEM' ||
      activeEvent?.type === 'DND_EDITOR_MULTIPLE_NO_CATEGORY_ITEMS' ||
      activeEvent?.type === 'DND_NAVIGATION_SUBCATEGORY' ||
      activeEvent?.type === 'DND_ACCORDION_NAVIGATION_CATEGORY'
    ) {
      return setDragOverlay(active.data.current?.dragOverlay);
    }
  }, []);

  const reset = useCallback(() => {
    setDragOverlay(null);
  }, []);

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={pointerWithin}
      onDragStart={handleDragStart}
      onDragEnd={reset}
      onDragCancel={reset}
    >
      {children}
      {createPortal(
        <DragOverlay
          adjustScale
          dropAnimation={{
            duration: 300,
          }}
          modifiers={[snapDragOverlayToPointer]}
        >
          <div>{dragOverlay}</div>
        </DragOverlay>,
        document.body,
      )}
    </DndContext>
  );
};

export default React.memo(MenuEditorDnDContext);
